import { AlertTriangle, KeyRound, RefreshCw, Send, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ApiRequestError, apiRequest } from "../api";

type DeletionRequest = {
  id: string;
  targetType: "HOUSEHOLD" | "INDIVIDUAL";
  recordId: string;
  memberIndex: number | null;
  targetLabel: string;
  reason: string;
  status: "PENDING" | "OTP_ISSUED" | "CONSUMED" | "EXPIRED" | "CANCELLED" | "STALE";
  otpExpiresAt: string | null;
};

export function SupervisorDeletionOtpActions({
  targetType,
  recordId,
  memberIndex,
  label,
  onDeleted,
}: {
  targetType: "HOUSEHOLD" | "INDIVIDUAL";
  recordId: string;
  memberIndex?: number;
  label: string;
  onDeleted(): void;
}) {
  const [activeRequest, setActiveRequest] = useState<DeletionRequest | null>(null);
  const [otp, setOtp] = useState("");
  const [requestFormOpen, setRequestFormOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const result = await apiRequest<{ requests: DeletionRequest[] }>("/deletion-requests/mine");
      const match = result.requests.find((item) =>
        item.recordId === recordId
        && item.targetType === targetType
        && (targetType === "HOUSEHOLD" || item.memberIndex === memberIndex)
        && ["PENDING", "OTP_ISSUED"].includes(item.status),
      ) ?? null;
      setActiveRequest(match);
      if (match?.status === "OTP_ISSUED") {
        setMessage("The MPO has issued a one-time code. Enter the 6-digit code below before it expires.");
      } else if (match?.status === "PENDING") {
        setMessage("Deletion request is pending. Ask the Municipal Population Officer (MPO) to generate the one-time code.");
      }
    } catch {
      // The record page remains usable even if authorization status is temporarily unavailable.
    }
  }, [memberIndex, recordId, targetType]);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  async function requestOtp() {
    setError("");
    setMessage("");
    const normalizedReason = reason.trim();
    if (normalizedReason.length < 10) {
      setError("Deletion reason must contain at least 10 characters.");
      return;
    }

    setLoading(true);
    try {
      const result = await apiRequest<DeletionRequest & { message: string }>("/deletion-requests", {
        method: "POST",
        body: JSON.stringify({ targetType, recordId, ...(targetType === "INDIVIDUAL" ? { memberIndex } : {}), reason: normalizedReason }),
      });
      setActiveRequest({ ...result, recordId, memberIndex: memberIndex ?? null, reason: normalizedReason, otpExpiresAt: null });
      setRequestFormOpen(false);
      setReason("");
      setMessage(result.message);
    } catch (requestError) {
      setError(requestError instanceof ApiRequestError ? requestError.message : "The deletion authorization request could not be submitted.");
    } finally {
      setLoading(false);
    }
  }

  function openDeleteConfirmation() {
    setError("");
    setMessage("");
    if (!activeRequest || !/^\d{6}$/.test(otp.trim())) {
      setError("Enter the 6-digit one-time code issued by the MPO.");
      return;
    }
    setConfirmDeleteOpen(true);
  }

  async function verifyAndDelete() {
    if (!activeRequest || !/^\d{6}$/.test(otp.trim())) {
      setConfirmDeleteOpen(false);
      setError("Enter the 6-digit one-time code issued by the MPO.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await apiRequest(`/deletion-requests/${activeRequest.id}/verify-and-delete`, {
        method: "POST",
        body: JSON.stringify({ otp: otp.trim() }),
      });
      setMessage(`${label} was deleted using the approved MPO one-time code. The action was audit logged.`);
      setConfirmDeleteOpen(false);
      setActiveRequest(null);
      setOtp("");
      onDeleted();
    } catch (requestError) {
      setError(requestError instanceof ApiRequestError ? requestError.message : "The deletion code could not be verified.");
      void loadStatus();
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="supervisor-deletion-otp" aria-label="MPO-authorized deletion">
      <h4><KeyRound /> MPO-authorized deletion</h4>
      <p className="muted-copy">Deletion requires a single-use code generated by the Municipal Population Officer for this exact record.</p>
      {!activeRequest ? (
        requestFormOpen ? (
          <div className="deletion-request-form">
            <div className="deletion-request-form-heading">
              <div>
                <strong>Request deletion authorization</strong>
                <p>Explain why <b>{label}</b> should be deleted. This reason will be saved in the RBIM audit trail and reviewed by the MPO.</p>
              </div>
            </div>
            <label>
              Deletion reason
              <textarea
                value={reason}
                onChange={(event) => setReason(event.currentTarget.value)}
                maxLength={1000}
                placeholder="Enter a clear reason for deleting this record..."
                autoFocus
              />
              <span className="deletion-reason-help">{reason.trim().length}/1000 characters · minimum 10 characters</span>
            </label>
            <div className="deletion-request-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={loading}
                onClick={() => { setRequestFormOpen(false); setReason(""); setError(""); }}
              >
                Cancel
              </button>
              <button
                className="danger-button"
                type="button"
                disabled={loading}
                onClick={() => void requestOtp()}
              >
                <Send />{loading ? "Submitting…" : "Submit request to MPO"}
              </button>
            </div>
          </div>
        ) : (
          <button className="danger-button" type="button" disabled={loading} onClick={() => { setRequestFormOpen(true); setError(""); setMessage(""); }}>
            <Send />Request deletion OTP
          </button>
        )
      ) : (
        <>
          <div className="notice-card">
            <strong>Request {activeRequest.status === "OTP_ISSUED" ? "authorized" : "pending"}</strong>
            <p>Request ID: <code>{activeRequest.id}</code></p>
            {activeRequest.otpExpiresAt ? <p>Code expires: {new Date(activeRequest.otpExpiresAt).toLocaleString("en-PH")}</p> : null}
          </div>
          <div className="record-action-row">
            <button className="secondary-button" type="button" disabled={loading} onClick={() => void loadStatus()}><RefreshCw />Refresh status</button>
          </div>
          {activeRequest.status === "OTP_ISSUED" ? (
            <>
              <div className="resident-edit-form deletion-otp-entry">
                <label>6-digit deletion code<input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} onChange={(event) => { setOtp(event.currentTarget.value.replace(/\D/g, "").slice(0, 6)); setConfirmDeleteOpen(false); }} placeholder="000000" /></label>
                <button className="danger-button" type="button" disabled={loading || otp.length !== 6} onClick={openDeleteConfirmation}><Trash2 />Verify OTP and delete</button>
              </div>

              {confirmDeleteOpen ? (
                <div className="otp-delete-confirmation-card" role="alertdialog" aria-labelledby="otp-delete-confirmation-title">
                  <div className="otp-delete-confirmation-heading">
                    <div className="otp-delete-confirmation-icon"><AlertTriangle /></div>
                    <div>
                      <span className="eyebrow">Final deletion confirmation</span>
                      <h3 id="otp-delete-confirmation-title">Delete this record now?</h3>
                      <p>This will permanently delete <strong>{label}</strong>. The one-time code will be consumed and cannot be reused.</p>
                    </div>
                  </div>

                  <div className="otp-delete-confirmation-grid">
                    <div><span>Target</span><strong>{label}</strong></div>
                    <div><span>Request ID</span><strong>{activeRequest.id}</strong></div>
                    <div><span>Entered OTP</span><strong>{otp}</strong></div>
                    <div><span>Code expires</span><strong>{activeRequest.otpExpiresAt ? new Date(activeRequest.otpExpiresAt).toLocaleString("en-PH") : "—"}</strong></div>
                  </div>

                  <div className="otp-delete-confirmation-warning">
                    <strong>Warning</strong>
                    <p>This action is irreversible. Confirm that this is the correct MPO-authorized request before continuing.</p>
                  </div>

                  <div className="otp-delete-confirmation-actions">
                    <button type="button" className="secondary-button" disabled={loading} onClick={() => setConfirmDeleteOpen(false)}>Cancel</button>
                    <button type="button" className="danger-button" disabled={loading} onClick={() => void verifyAndDelete()}><Trash2 />{loading ? "Deleting…" : "Confirm delete now"}</button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </>
      )}
      {message ? <div className="success-message" role="status">{message}</div> : null}
      {error ? <div className="form-error" role="alert">{error}</div> : null}
    </section>
  );
}
